import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Check } from "lucide-react";
import customWebsite from "@/assets/custom-website.png";
import customMobileApp from "@/assets/custom-mobile-app.png";
import customScoring from "@/assets/custom-scoring.png";

interface MousePosition {
  x: number;
  y: number;
}

const CustomSolutionsSection = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const relativeScroll = window.scrollY - sectionTop + window.innerHeight;
        setScrollY(relativeScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (imagesContainerRef.current) {
      const rect = imagesContainerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
      const y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
      setMousePosition({ x, y });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const get3DTransform = (
    baseRotation: number,
    parallaxOffset: number,
    intensity: number = 1
  ) => {
    const rotateX = -mousePosition.y * 15 * intensity;
    const rotateY = mousePosition.x * 15 * intensity;
    return `
      perspective(1000px)
      translateY(${parallaxOffset}px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      rotate(${baseRotation}deg)
    `;
  };

  const features = [
    t("custom.home.feature1"),
    t("custom.home.feature2"),
    t("custom.home.feature3"),
    t("custom.home.feature4"),
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-secondary/30 overflow-hidden">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="space-y-6 order-2 lg:order-1">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              {t("custom.label")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {t("custom.home.title")}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("custom.home.description")}
            </p>
            
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            <Button asChild size="lg" className="mt-4">
              <Link to="/solutions/custom">{t("custom.cta")}</Link>
            </Button>
          </div>

          {/* Right - Parallax Screenshots */}
          <div 
            ref={imagesContainerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative h-[450px] md:h-[550px] cursor-pointer order-1 lg:order-2"
            style={{ perspective: "1000px" }}
          >
            {/* Website Screenshot */}
            <div
              className="absolute right-[0%] top-[5%] w-48 h-36 md:w-64 md:h-48 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ease-out border border-border/50"
              style={{
                transform: get3DTransform(4, scrollY * 0.06, 1.2),
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src={customWebsite}
                alt="Custom Website Development"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/15 pointer-events-none" />
            </div>

            {/* Mobile App Screenshot */}
            <div
              className="absolute left-[5%] top-[15%] w-44 h-32 md:w-60 md:h-44 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ease-out border border-border/50"
              style={{
                transform: get3DTransform(-5, scrollY * 0.1, 0.9),
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src={customMobileApp}
                alt="Mobile App Development"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/15 pointer-events-none" />
            </div>

            {/* Scoring System Screenshot */}
            <div
              className="absolute right-[10%] bottom-[5%] w-52 h-40 md:w-72 md:h-52 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ease-out border border-border/50"
              style={{
                transform: get3DTransform(-3, scrollY * 0.04, 1),
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src={customScoring}
                alt="Scoring Decision System"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/15 pointer-events-none" />
            </div>

            {/* Decorative gradient circles */}
            <div
              className="absolute right-[40%] top-[0%] w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 opacity-30 blur-xl transition-transform duration-500"
              style={{
                transform: `translateY(${scrollY * -0.05}px) translateX(${mousePosition.x * 20}px)`,
              }}
            />
            <div
              className="absolute left-[10%] bottom-[20%] w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 opacity-25 blur-xl transition-transform duration-500"
              style={{
                transform: `translateY(${scrollY * -0.08}px) translateX(${mousePosition.x * -15}px)`,
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutionsSection;
